<script setup lang="ts">
import { useCommissioningDeviceStore } from '@/modules/commissioning/stores/commissioningDeviceStore';
import { useDeviceSpecificationStore } from '@/modules/commissioning/stores/deviceSpecificationStore';
import type { CommissionResult } from '@/modules/commissioning/models/CommissioningDevice';
import ProgressFrame from '@/modules/commissioning/views/layout/ProgressFrame.vue';
import VisualFailure from '@/modules/commissioning/components/VisualFailure.vue';
import ErrorSummaryCard from '@/modules/commissioning/components/ErrorSummaryCard.vue';
import {
  getTerminalGroupingWithErrorsFromCommissionResult,
  getCommissionResultConfigValues,
} from '@/modules/commissioning/utils/error-resolver';
import {
  type ComponentErrorType,
  ComponentErrorTypeDescription,
} from '@/modules/commissioning/utils/error-constants';
import { useRouter } from 'vue-router';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import ButtonOutlined from '@/shared/components/button/ButtonOutlined.vue';
import { isEmptyObject } from '@/utils/object';
import { ERROR_CTA_BUTTONS } from '@/modules/commissioning/utils/error-constants';
import { useCommissioningNavStore } from '@/modules/commissioning/stores/commissioningNavStore';
import { COMMISSION_RESULT_VERDICT } from '@/modules/commissioning/utils/constants';

const commissioningNavStore = useCommissioningNavStore();
const deviceStore = useCommissioningDeviceStore();
const deviceSpecificationStore = useDeviceSpecificationStore();
const connections = deviceSpecificationStore.solshareSystemSpec.connections;
const commissionResult = deviceStore.commissionResultStatus.results ?? ({} as CommissionResult);
const terminalErrorGrouping = getTerminalGroupingWithErrorsFromCommissionResult(
  commissionResult,
  connections,
);
const uniqueErrorTypes = Object.keys(terminalErrorGrouping) as ComponentErrorType[];
const commissionResultFailIds = getCommissionResultConfigValues(commissionResult, 'failId');
const commissionResultCtaButtons = getCommissionResultConfigValues(commissionResult, 'ctaButtons');

/** Any terminal failed — includes reasons not mapped to a troubleshooting page (e.g. future API codes). */
const commissionResultHasFailure = Object.values(commissionResult).some(
  (r) => r.verdict === COMMISSION_RESULT_VERDICT.FAIL,
);
/**
 * Show the same generic summary as an empty payload when we cannot map failures to error UI
 * (SW-3323: unmapped fail reasons produced a blank screen).
 */
const showGenericErrorSummary =
  isEmptyObject(commissionResult) ||
  (uniqueErrorTypes.length === 0 && commissionResultHasFailure);

/** Mapped errors derive CTAs from COMMISSION_RESULT_CONFIGS; generic fallback offers retry/reconfigure only. */
const ctaButtonsFromMappedErrors = [
  ...new Set(
    uniqueErrorTypes.flatMap((errorType) => commissionResultCtaButtons[errorType] ?? []),
  ),
];
const genericErrorSummaryCtaButtons: ERROR_CTA_BUTTONS[] = [
  ERROR_CTA_BUTTONS.RETRY_COMMISSIONING,
  ERROR_CTA_BUTTONS.RECONFIGURE,
];
const ctaButtons = showGenericErrorSummary
  ? genericErrorSummaryCtaButtons
  : ctaButtonsFromMappedErrors;

const router = useRouter();
const retry = () => {
  deviceStore.isUploadConfig = false;
  router.push({ name: 'StartCommissioning' });
};
const reconfigure = () => router.push({ name: 'ConfigureSolshareSystemSpec' });

const onSelectErrorType = (errorType: ComponentErrorType) => {
  // At the moment all action buttons go to the troubleshoot page.
  // This can be modified per error type in the future.
  router.push({ name: 'Troubleshoot', params: { errorType } });
};

const onContactSupportClick = () => {
  commissioningNavStore.requestContactUsViaNavBar();
};
</script>

<template>
  <ProgressFrame pageClass="fullfocus-page" backgroundColor="bg-dark" withBackArrow>
    <div class="content">
      <template v-if="uniqueErrorTypes.length > 0">
        <VisualFailure>
          <template #title>We identified connections needing attention</template>
        </VisualFailure>
        <div class="error-summary-cards" v-if="uniqueErrorTypes.length > 0">
          <ErrorSummaryCard
            v-for="errorType in uniqueErrorTypes.sort(
              (a, b) =>
                (ComponentErrorTypeDescription[a]?.order ?? 0) -
                (ComponentErrorTypeDescription[b]?.order ?? 0),
            )"
            :key="errorType"
            :title="ComponentErrorTypeDescription[errorType].title"
            :subtitle="ComponentErrorTypeDescription[errorType].subtitle"
            :terminals="terminalErrorGrouping[errorType] ?? []"
            :failIds="commissionResultFailIds[errorType] ?? []"
            :actionButtonText="ComponentErrorTypeDescription[errorType].buttonText"
            :data-tn="'error-summary-card-' + ComponentErrorTypeDescription[errorType].testId"
            :contactButtonText="ComponentErrorTypeDescription[errorType].contactButtonText"
            :noteText="ComponentErrorTypeDescription[errorType].noteText"
            @action-click="onSelectErrorType(errorType)"
            @contact-support-click="onContactSupportClick()"
          />
        </div>
      </template>
      <template v-else-if="showGenericErrorSummary">
        <VisualFailure>
          <template #title>Something went wrong</template>
        </VisualFailure>
        <div class="error-summary-cards">
          <ErrorSummaryCard
            title="Please retry commissioning or reconfigure your device."
            key="default-error-summary-card"
            data-tn="default-error-summary-card"
            :terminals="[]"
            :failIds="[]"
          />
        </div>
      </template>
    </div>

    <div class="error-cta-buttons" v-if="ctaButtons.length > 0">
      <ButtonPrimary
        block
        class="error-cta-button"
        data-tn="retry"
        @click="retry"
        v-if="ctaButtons.includes(ERROR_CTA_BUTTONS.RETRY_COMMISSIONING)"
      >
        {{
          !isEmptyObject(commissionResult) ? 'Fixed? Re-try commissioning' : 'Re-try commissioning'
        }}
      </ButtonPrimary>
      <ButtonOutlined
        block
        class="error-cta-button"
        data-tn="reconfigure"
        @click="reconfigure"
        v-if="ctaButtons.includes(ERROR_CTA_BUTTONS.RECONFIGURE)"
      >
        Reconfigure
      </ButtonOutlined>
    </div>
  </ProgressFrame>
</template>

<style scoped lang="less">
.content {
  flex-grow: 1;
}

.error-summary-cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.error-cta-buttons {
  margin-top: 2rem;
  border-top: 1px solid @color-mid-tone;
  padding: @content-side-margin;
  background-color: white;
  flex-grow: 1;
}
</style>
